import { useSnackbar } from 'notistack';
import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
// form
import { yupResolver } from '@hookform/resolvers/yup';
import { Controller, useForm } from 'react-hook-form';
// @mui
import { LoadingButton } from '@mui/lab';
import { Autocomplete, Button, ButtonGroup, Chip, Grid, Stack, TextField, Typography } from '@mui/material';
import { styled } from '@mui/material/styles';
// routes
// components
import { FormProvider, RHFEditor, RHFSwitch, RHFTextField, RHFUploadSingleFile } from '../../../components/hook-form';
//
import { BlogUtils } from '../../../api_utils';
import useAuth from '../../../hooks/useAuth';
import BlogNewPostPreview from './BlogNewPostPreview';

// ----------------------------------------------------------------------

const TAGS_OPTION = [
  'Update',
  'News',
  'Information'
];

const LabelStyle = styled(Typography)(({ theme }) => ({
  ...theme.typography.subtitle2,
  color: theme.palette.text.secondary,
  marginBottom: theme.spacing(1),
}));

// ----------------------------------------------------------------------

export default function BlogNewPostForm({ post, successCallback, onClose }) {
  const navigate = useNavigate();

  const { user } = useAuth();

  const [open, setOpen] = useState(false);

  const { enqueueSnackbar } = useSnackbar();

  const handleOpenPreview = () => {
    setOpen(true);
  };

  const handleClosePreview = () => {
    setOpen(false);
  };

  const NewBlogSchema = Yup.object().shape({
    title: Yup.string().required('Title is required'),
    subtitle: Yup.string().required('Subtitle is required'),
    content: Yup.string().required('Content is required'),
    imageUrl: Yup.string().required('Image/Cover is required'),
  });

  const defaultValues = {
    id: post.id,
    title: post.title,
    subtitle: post.subtitle,
    content: post.content,
    imageUrl: post.imageUrl,
    tags: post.tags,
    published: post.published,
    comments_enabled: post.comments_enabled,
  };

  const methods = useForm({
    resolver: yupResolver(NewBlogSchema),
    defaultValues,
  });

  const {
    reset,
    watch,
    control,
    setValue,
    handleSubmit,
    formState: { isSubmitting, isValid },
  } = methods;

  const values = watch();

  const onSubmit = async () => {
    try {
      let res;
      if (values.id === -1) {
        // new post
        res = await BlogUtils.addPost({ ...values, user_id: user.id });
      } else {
        res = await BlogUtils.updatePost({ ...values, user_id: user.id });
      }

      if (res.success) {
        enqueueSnackbar(`Post has been successfully ${values.id === -1 ? "created" : "updated"}!`)
      } else {
        enqueueSnackbar(`There was a problem ${values.id === -1 ? "creating" : "updating"} the post: ${res.message}`, { variant: 'warning' })
      }
    } catch (error) {
      console.error(error);
    }
  };

  const handleDrop = useCallback(
    (acceptedFiles) => {
      const file = acceptedFiles[0];
      if (file) {
        const openFile = function (file) {
          const reader = new FileReader();
          reader.onload = function () {
            setValue(
              'imageUrl',
              reader.result,
            );
          };
          reader.readAsDataURL(file);
        };
        openFile(file);
      }
    },
    [setValue]
  );


  return (
    <>
      <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={3} sx={{ p: 3 }}>
          <Grid item xs={12} md={8}>
            <Stack spacing={3} >
              <RHFTextField name="title" label="Post Title" />

              <RHFTextField name="subtitle" label="Subtitle" />

              <div>
                <LabelStyle>Content</LabelStyle>
                <RHFEditor name="content" />
              </div>
            </Stack>
          </Grid>

          <Grid item xs={12} md={4}>
            <Stack spacing={3} >

              {post.id === -1 &&
                <Controller
                  name="tags"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      multiple
                      freeSolo
                      onChange={(event, newValue) => field.onChange(newValue)}
                      options={TAGS_OPTION.map((option) => option)}
                      renderTags={(value, getTagProps) =>
                        value.map((option, index) => (
                          <Chip {...getTagProps({ index })} key={option} size="small" label={option} />
                        ))
                      }
                      renderInput={(params) => <TextField label="Tags" {...params} />}
                    />
                  )}
                />
              }
              <div>
                <RHFSwitch
                  name="published"
                  label="Published"
                  labelPlacement="start"
                  sx={{ mb: 1, mx: 0, width: 1, justifyContent: 'space-between' }}
                />

                <RHFSwitch
                  name="comments_enabled"
                  label="Enable comments"
                  labelPlacement="start"
                  sx={{ mx: 0, width: 1, justifyContent: 'space-between' }}
                />
              </div>

              <div>
                <LabelStyle>{values.id !== -1 && "Update"} Cover</LabelStyle>
                <RHFUploadSingleFile name="imageUrl" accept="image/*" maxSize={3145728} onDrop={handleDrop} />
              </div>
            </Stack>

            <ButtonGroup sx={{ mt: 3 }} fullWidth>
              <Button fullWidth color="warning" variant="contained" size="large" onClick={onClose}>
                Close
              </Button>
              <Button fullWidth color="secondary" variant="contained" size="large" onClick={handleOpenPreview}>
                Preview
              </Button>
              <LoadingButton fullWidth type="submit" variant="contained" size="large" loading={isSubmitting}>
                {values.id === -1 ? "Post" : "Update"}
              </LoadingButton>
            </ButtonGroup>
          </Grid>
        </Grid>
      </FormProvider>

      <BlogNewPostPreview
        values={values}
        isOpen={open}
        isValid={isValid}
        isSubmitting={isSubmitting}
        onClose={handleClosePreview}
        onSubmit={handleSubmit(onSubmit)}
      />
    </>
  );
}
